import { Card } from 'antd';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { RawIntlProvider } from 'react-intl';
import { useParams } from 'react-router-dom-6';

//Context

//Pages
import ConfirmIdentity from './Pages/ConfirmIdentity';
import Minimal from './Pages/Minimal';
import Refresh from './Pages/Refresh';
import Vaccine from './Pages/Vaccine';
import TokenViewContext from './TokenViewContext';
import styles from './index.module.less';

import { ErrorMessage, Spinner } from '$HVUI';
import { useBeacon, useFetch, useIntlHelper } from '$Hooks';
import { flexibleDateString, queryStringFromObject } from '$Utils';

export const bilingualError = ({
    englishMessage = '',
    spanishMessage = ''
}) => (
    <div className={styles['error-message']}>
        <p>{englishMessage || 'Sorry, something went wrong.'}</p>
        <p>
            If you continue to have difficulty, please visit our{' '}
            <a href="https://healthvana.com/faq/">FAQ page</a>.
        </p>
        <p lang="es">{spanishMessage || 'Disculpe, algo salió mal.'}</p>
        <p lang="es">
            Si sigue teniendo dificultades, por favor visite nuestra{' '}
            <a href="https://healthvana.com/faq/">
                página de preguntas frecuentes
            </a>
            .
        </p>
    </div>
);

// these very specifically are functions because some of them reflect html
export const unrecoverableError = ({
    englishMessage = null,
    spanishMessage = null
}) => (
    <div className={styles['error-message']}>
        <p>
            {typeof englishMessage === 'function'
                ? englishMessage()
                : 'Sorry, something went wrong.'}
        </p>
        <p lang="es">
            {typeof spanishMessage === 'function'
                ? spanishMessage()
                : 'Disculpe, algo salió mal.'}
        </p>
    </div>
);

const contactFormErrorMessage = {
    englishMessage: () => (
        <>
            Sorry, your result can't be retrieved. Please use our{' '}
            <a href="https://healthvana.com/contact/">contact form</a> to get
            help.
        </>
    ),
    spanishMessage: () => (
        <>
            Disculpe, no se pudo recuperar su resultado. Por favor utilice
            nuestro{' '}
            <a href="https://healthvana.com/contact/">impreso de contacto</a>{' '}
            para buscar ayuda.
        </>
    )
};

const throttledErrorMessage = {
    englishMessage: () => (
        <>
            Maximum view rate exceeded. Please wait a few minutes and try again.
        </>
    ),
    spanishMessage: () => (
        <>
            Ratio máximo de visitas superado. Por favor espere unos minutos e
            intente de nuevo.
        </>
    )
};

const Index = () => {
    // UUID token from URL through the URL params
    const { token } = useParams();

    const [patientInformation, setPatientInformation] = useState();
    const [identityVerified, setIdentityVerified] = useState(false);
    const [visitType, setVisitType] = useState();
    const [visit, setVisit] = useState();
    const [vaccinations, setVaccinations] = useState();
    const [resetURL, setResetURL] = useState();
    const [needsRefresh, setNeedsRefresh] = useState();
    const [showContactTracing, setShowContactTracing] = useState();
    const [contactTraceReminder, setContactTraceReminder] = useState();
    const [error, setError] = useState(null);
    const [initialValues, setInitialValues] = useState();
    const [showGooglePassDownload, setShowGooglePassDownload] = useState();
    const [showApplePassDownload, setShowApplePassDownload] = useState();
    const [
        contactTraceSubmittedThisSession,
        setContactTraceSubmittedThisSession
    ] = useState();

    // failed flag indicates we're non-recoverable.
    //   it will prevent any UI other than error card from showing.
    const [failed, setFailed] = useState();
    const {
        ourIntl,
        updateIntl,
        loading: intlLoading
    } = useIntlHelper({
        shouldIntl: true,
        appName: 'TokenView'
    });
    const [identityFailCount, setIdentityFailCount] = useState(0);

    // Set Beacon's token to null to deactivate it if we're in testing
    useBeacon('e8f918c6-0980-41ee-8ae0-a4c5d6bdb278');
    // Open Beacon if identity confirmation fails 3 times
    useEffect(() => {
        if (identityFailCount == 3) {
            window.Beacon?.('open');
        }
    }, [identityFailCount]);

    // Token Status fetch
    const { data: tokenStatusData } = useFetch(
        `/api/v2/results/token-results/token-status/?token=${token}`,
        {},
        []
    );

    // Configure which endpoint we're using
    const endpoint =
        tokenStatusData?.visit_token_type === 'vaccine'
            ? 'vaccinations'
            : 'results';

    // Token Result or Vaccination Record fetch
    const { data: resultData, get: getResults } = useFetch(
        `/api/v2/results/token-${endpoint}/${token}/`,
        {
            cachePolicy: 'no-cache'
        }
    );

    // Token Status hook
    useEffect(() => {
        if (!tokenStatusData) return;
        setInitialValues({
            first_name: tokenStatusData.first_initial,
            last_name: tokenStatusData.last_initial
        });
        setVisitType(tokenStatusData.visit_token_type);

        // Currently throttled at 5 requests/min
        // endpoint gives us a message and an expected available time
        if (tokenStatusData?.detail?.includes('throttled')) {
            setError(unrecoverableError(throttledErrorMessage));
            setFailed(true);
            return;
        }
        // if we don't get a status in the response
        if (!tokenStatusData.status) {
            setError(bilingualError({}));
            setFailed(true);
            return;
        }
        // if UUID doesn't match any token in our DB
        if (tokenStatusData.status === 'token not found') {
            setError(unrecoverableError(contactFormErrorMessage));
            setFailed(true);
            return;
        }
        // if token is marked as deactivated
        if (tokenStatusData.status === 'deactivated') {
            setError(unrecoverableError(contactFormErrorMessage));
            setFailed(true);
            return;
        }
        // if we're past the token's 'expires_datetime'
        // OR if the status is anything other than 'generated'
        if (
            tokenStatusData.status === 'expired' ||
            tokenStatusData.status === 'consumed'
        ) {
            setNeedsRefresh(true);
            return;
        }
        // if this is a group result delivery, which we don't allow anymore
        // so it errors out in the resultData hook below
        if (tokenStatusData.status === 'exempt') {
            // retaining this so we can accurately fire GA events around this for a bit
            setIdentityVerified(true);
            // load results
            return;
        }
        //if status === 'nonexempt', we automatically show identity form
    }, [tokenStatusData]);

    //Token Result hook
    useEffect(() => {
        if (!resultData) {
            setIdentityFailCount(identityFailCount + 1);
            return;
        }

        if (resultData.detail) {
            if (identityVerified) {
                // retaining this to track exempt attempts
                setNeedsRefresh(true);
            } else {
                setError(
                    bilingualError({
                        englishMessage:
                            'Sorry, this information did not match.',
                        spanishMessage:
                            'Disculpe, esta información no coincidió.'
                    })
                );
                setIdentityFailCount(identityFailCount + 1);
            }
            return;
        }
        setIdentityVerified(true);
        setVisit(resultData.visit);
        setVaccinations(resultData.vaccinations);
        setResetURL(resultData.reset_url);
        setShowContactTracing(
            resultData.show_contact_tracing &&
                !resultData.contact_trace_submitted
        );
        setContactTraceReminder(
            resultData.show_contact_tracing && resultData.contact_trace_reminder
        );
        setShowApplePassDownload(
            resultData.show_results_apple_pass_download_link
        );
        setShowGooglePassDownload(
            resultData.show_results_google_pass_download_link
        );
    }, [resultData]);

    // Handle identity confirm submit
    const loadResults = async (
        { birthdate, first_name, last_name },
        formik
    ) => {
        const parsedBirthdate = moment(birthdate, ['MM/DD/YYYY', 'DD/MM/YYYY']);
        const isoBirthdate = flexibleDateString(parsedBirthdate, null, 'ISO');
        setIdentityFailCount(0);
        const data = {};
        if (isoBirthdate) data.birthdate = isoBirthdate;
        if (first_name) data.first_name = first_name;
        if (last_name) data.last_name = last_name;
        const beaconPrefill = {
            name: `${first_name} ${last_name}`,
            text: `I'm having trouble accessing my result. First name ${first_name}, last name ${last_name}, birthdate ${birthdate}.`
        };

        const resultsData = await getResults(queryStringFromObject(data));

        //Stop spinner on submit button
        if (formik) {
            formik.setSubmitting(false);
        }

        if (!resultsData) {
            window.Beacon?.('prefill', beaconPrefill);
            return;
        }

        setPatientInformation({
            first_name: first_name,
            last_name: last_name,
            birthdate: isoBirthdate
        });

        if (resultsData.detail) {
            if (!identityVerified) {
                window.Beacon?.('prefill', beaconPrefill);
            }
        }
    };

    const wrapper = body => (
        <div className={styles['wrapper']}>
            <Card className={styles.card}>{body}</Card>
        </div>
    );

    const content = () => {
        if (!tokenStatusData || intlLoading) {
            return wrapper(<Spinner size={60} fullsize />);
        }

        if (needsRefresh) {
            return <Refresh token={token} />;
        }

        if (failed) {
            return wrapper(
                <ErrorMessage errorMessage={error} bordered={false} />
            );
        }

        if (!identityVerified) {
            return (
                <ConfirmIdentity
                    updateIntl={updateIntl}
                    error={error}
                    handleSubmit={loadResults}
                    initialValues={initialValues}
                />
            );
        }

        if (!visit || (visitType === 'vaccine' && !vaccinations)) {
            return (
                <div className={styles['spinner-container']}>
                    <Spinner size={60} />
                </div>
            );
        }

        if (visitType === 'vaccine') {
            return (
                <Vaccine
                    resetURL={resetURL}
                    vaccinations={vaccinations}
                    visit={visit}
                    token={token}
                />
            );
        }
        return (
            <Minimal
                token={token}
                resetURL={resetURL}
                visit={visit}
                showContactTracing={showContactTracing}
                contactTraceReminder={contactTraceReminder}
                showGooglePassDownload={showGooglePassDownload}
                showApplePassDownload={showApplePassDownload}
                applePassUUID={resultData.results_apple_pass_public_uuid}
                googlePassUUID={resultData.results_google_pass_public_uuid}
            />
        );
    };

    return (
        <RawIntlProvider value={ourIntl}>
            <TokenViewContext.Provider
                value={{
                    ...patientInformation,
                    contactTraceSubmittedThisSession,
                    setContactTraceSubmittedThisSession
                }}
            >
                {content()}
            </TokenViewContext.Provider>
        </RawIntlProvider>
    );
};

export default Index;
